import React, { useState } from 'react';
import Modal from 'components/Modal/Modal';
import { Input } from '../DesignLibrary/Atoms/Input';
import Button from '../DesignLibrary/Button';
import s from './AddExemptionsModal.module.scss';
import { Select, TextArea } from '../DesignLibrary/Atoms';

export interface AddExemptionsFormData {
  description: string;
  exemptionSystem: 'state' | 'federal';
  exemptionLaws: string;
  exemptionClaim: 'customDollarAmount' | 'fairMarketValue';
  amount: string;
}

interface AddExemptionsProps {
  optional?: boolean;
  onSave?: (data: AddExemptionsFormData) => void;
  onCancel?: () => void;
  initialValues?: Partial<AddExemptionsFormData>;
}

interface AddExemptionsModalProps extends Omit<AddExemptionsProps, 'onSave' | 'onCancel'> {
  open: boolean;
  onClose: () => void;
  onSave: (data: AddExemptionsFormData) => void;
}

const exemptionSystems = [
  { label: 'State', value: 'state' },
  { label: 'Federal', value: 'federal' },
];

const exemptionClaims = [
  { label: 'Custom Dollar Amount', value: 'customDollarAmount' },
  { label: '100% of the fair market value', value: 'fairMarketValue' },
];

const AddExemptionsModal: React.FC<AddExemptionsModalProps> = ({ open, onClose, onSave, optional }) => {
  const [description, setDescription] = useState('');
  const [exemptionSystem, setExemptionSystem] = useState<'state' | 'federal'>('state');
  const [exemptionLaws, setExemptionLaws] = useState('');
  const [exemptionClaim, setExemptionClaim] = useState<'customDollarAmount' | 'fairMarketValue'>('customDollarAmount');
  const [amount, setAmount] = useState('');

  const handleSave = (): void => {
    if (onSave) {
      onSave({
        amount,
        description,
        exemptionClaim,
        exemptionLaws,
        exemptionSystem,
      });

      // Reset all form values
      setDescription('');
      setExemptionSystem('state');
      setExemptionLaws('');
      setExemptionClaim('customDollarAmount');
      setAmount('');
    }
  };

  if (!open) return null;

  return (
    <Modal onClose={onClose} title="Add New Exemption">
      <div className={s.modalContainer}>
        <TextArea compact label="1. Schedule C: Brief description (autofilled)" value={description} onChange={setDescription} optional={optional} />
        <Select
          compact
          options={exemptionSystems.map(opt => ({ key: opt.value, label: opt.label, value: opt.value }))}
          label="2. Which exemption system do you prefer to use for this exemption?"
          optional={optional}
          value={exemptionSystem}
          onChange={v => setExemptionSystem(v as 'state' | 'federal')}
          canClearSelection={false}
        />
        <Input
          compact
          label="3. Specific laws that allow exemption (Statute)"
          value={exemptionLaws}
          onChange={setExemptionLaws}
          placeholder="e.g., 11 U.S.C. § 522(d)(5)"
          optional={optional}
        />
        <Select
          compact
          options={exemptionClaims.map(opt => ({ key: opt.value, label: opt.label, value: opt.value }))}
          label="4. Type of exemption claim"
          optional={optional}
          value={exemptionClaim}
          onChange={v => setExemptionClaim(v as 'customDollarAmount' | 'fairMarketValue')}
          canClearSelection={false}
        />
        <Input compact label="5. Amount of exemption you claim" value={amount} onChange={setAmount} placeholder="$0.00" optional={optional} />
        <Button
          onClick={handleSave}
          variant="primary"
          size="md"
          fullWidth
          disabled={!description || !exemptionLaws || !amount}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default AddExemptionsModal;
